import './styles.scoped.css'
import {useEffect} from 'react'
import {Typography, Button, IconButton, Modal, TextField, Avatar, Chip} from '@mui/material'
import {Close, DateRange, AccessTime} from '@mui/icons-material'
import {Controller, useForm} from 'react-hook-form'
import {yupResolver} from '@hookform/resolvers/yup'
import * as yup from 'yup'
import {useMobile} from '../../hooks'
import {getColorFromName, getInitials, ignoreTimezone, parseError, formatTime, zonedWallTimeToUtcIso} from '../../utils/helpers'
import {reportTimes} from '../../utils/visitActions'
import {DatePicker} from '@mui/x-date-pickers/DatePicker'
import {format} from 'date-fns'
import {scheduleApi} from '../../services'
import {useGlobalStates} from '../../contexts/GlobalStatesContext'
import {VisitDialogCardSkeleton} from '../Skeletons'

export default ({open, setOpen, loadData, items = [], loading = false}) => {
    const {mobile} = useMobile()
    const {setLoading, setMessageBox, branchId} = useGlobalStates()
    const form = useForm({
        resolver: yupResolver(
            yup.object().shape({
                items: yup.array().of(
                    yup.object().shape({
                        id: yup.string(),
                        date: yup.date(),
                        start_time: yup.string(),
                        end_time: yup.string().required('End time is required'),
                    }),
                ),
            }),
        ),
    })

    // Fill the form when the dialog opens, or when it is handed different visits. Not whenever
    // `items` changes: the parents pass a fresh array on every render (and render again on any
    // global state change, such as a toast clearing itself), which would wipe what was typed.
    const visitIds = items?.map(item => item?.id).join(',')
    useEffect(() => {
        if (open && items?.length > 0) {
            form.setValue(
                'items',
                // The end is the recorded clock-out where there is one, the scheduled end otherwise.
                items?.map(item => ({...item, date: new Date(ignoreTimezone(item?.date)), end_time: reportTimes(item).end_time})),
            )
        }
    }, [open, visitIds])

    const submit = data => {
        setLoading(true)
        scheduleApi
            .clockOut(branchId, items?.[0]?.schedule_id, {
                visits: data?.items?.map((item, index) => ({
                    id: item?.id,
                    // The typed time is wall-clock time in the schedule's zone, not UTC.
                    clock_out_time: zonedWallTimeToUtcIso(
                        format(new Date(item?.date), 'yyyy-MM-dd'),
                        item?.end_time,
                        items?.[index]?.schedule?.timezone,
                    ),
                })),
            })
            .then(() => {
                setLoading(false)
                setOpen(false)
                if (items?.length == 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Clock Out Recorded',
                        description: `The appointment for ${items[0]?.schedule?.caregiver?.first_name + ' ' + items[0]?.schedule?.caregiver?.last_name} has been successfully marked as Ended.`,
                    })
                } else if (items?.length > 1) {
                    setMessageBox({
                        status: 'success',
                        title: 'Clock Outs Recorded',
                        description: `Clock out has been successfully recorded for ${items?.length} schedules.`,
                    })
                }
                loadData()
            })
            .catch(error => {
                setLoading(false)
                setOpen(false)
                const {title, description} = parseError(error)
                if (description) {
                    setMessageBox({status: 'error', title, description})
                }
            })
    }

    return (
        <Modal open={open}>
            <div className="container">
                <div className="content">
                    {mobile ? (
                        <>
                            <div className="header">
                                <Typography variant="h6" color="text.primary">
                                    {items?.length > 1 ? 'Confirm Clock Out for Multiple Schedules' : 'Confirm Clock Out'}
                                </Typography>
                                <IconButton onClick={() => setOpen(false)}>
                                    <Close />
                                </IconButton>
                            </div>
                            <div className="actions">
                                <Button variant="outlined" onClick={() => setOpen(false)} style={{flex: 1}}>
                                    Cancel
                                </Button>
                                <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} disabled={loading} style={{flex: 1}}>
                                    Confirm Clock-Out {items?.length > 1 ? `(${items?.length})` : ''}
                                </Button>
                            </div>
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <Typography variant="h6" color="text.primary" className="title">
                            {items?.length > 1 ? 'Confirm Clock Out for Multiple Schedules' : 'Confirm Clock Out'}
                        </Typography>
                    )}
                    <br />
                    <Typography variant="body2" color="text.primary" className="title">
                        {items?.length > 1
                            ? `You are about to record the Clock Out for the following ${items?.length} schedules. Please review the list before confirming:`
                            : 'You are about to record the Clock Out for this schedule. Please review the details below before confirming:'}
                    </Typography>
                    {items.map((item, index) =>
                        loading ? (
                            <div key={index}>
                                <br />
                                <VisitDialogCardSkeleton section="reportTime" />
                            </div>
                        ) : (
                            <div key={index}>
                                <br />
                                <div className="card">
                                    <div className="card-profile">
                                        <div className="people-card">
                                            {item?.schedule?.client?.avatar_url ? (
                                                <Avatar src={item?.schedule?.client?.avatar_url} style={{width: 40, height: 40}} />
                                            ) : (
                                                <Avatar
                                                    style={{
                                                        backgroundColor: getColorFromName(
                                                            item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name,
                                                        ),
                                                        width: 40,
                                                        height: 40,
                                                        fontSize: 20,
                                                    }}>
                                                    {getInitials(item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name)}
                                                </Avatar>
                                            )}
                                            <div className="people-profile-card">
                                                <Typography variant="h8" color="text.primary" className="name">
                                                    {item?.schedule?.client?.first_name + ' ' + item?.schedule?.client?.last_name}
                                                </Typography>
                                                <Typography variant="body3" color="text.secondary" className="name">
                                                    {item?.schedule?.service?.name}
                                                </Typography>
                                            </div>
                                        </div>
                                        {item?.status === 'scheduled' && (
                                            <Chip
                                                label="Scheduled"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: theme.palette.grey[700],
                                                    color: theme.palette.secondary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'in_progress' && <Chip label="In progress" variant="filled" color="warning" size="small" />}
                                        {item?.status === 'ended' && (
                                            <Chip
                                                label="Ended"
                                                variant="filled"
                                                sx={theme => ({
                                                    backgroundColor: '#02CAD1',
                                                    color: theme.palette.primary.contrastText,
                                                })}
                                                size="small"
                                            />
                                        )}
                                        {item?.status === 'finished' && <Chip label="Finished" variant="filled" color="info" size="small" />}
                                        {item?.status === 'completed' && <Chip label="Completed" variant="filled" color="success" size="small" />}
                                        {item?.status === 'cancelled' && <Chip label="Cancelled" variant="filled" color="error" size="small" />}
                                    </div>
                                    <br />
                                    <div className="datetime">
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <DateRange fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {format(new Date(ignoreTimezone(item?.date)), 'EEE, dd MMM yyyy')}
                                            </Typography>
                                        </div>
                                        <Typography variant="body3" color="text.secondary">
                                            |
                                        </Typography>
                                        <div className="row-content">
                                            <Typography variant="body3" color="text.secondary">
                                                <AccessTime fontSize="small" />
                                            </Typography>
                                            <Typography variant="body3" color="text.secondary">
                                                {formatTime(item?.start_time)} - {formatTime(item?.end_time)}
                                            </Typography>
                                        </div>
                                    </div>
                                    <br />
                                    <Typography variant="h8" color="text.primary">
                                        Clock-Out Time
                                    </Typography>
                                    <br />
                                    <br />
                                    <Typography variant="inputLabel" color="text.secondary">
                                        Date
                                    </Typography>
                                    <Controller
                                        {...form.register(`items.${index}.date`)}
                                        control={form.control}
                                        render={({field}) => (
                                            <DatePicker
                                                placeholder="Enter your date"
                                                {...field}
                                                value={field.value ?? null}
                                                onChange={newValue => field.onChange(newValue)}
                                                enableAccessibleFieldDOMStructure={false}
                                                slots={{
                                                    textField: params => <TextField {...params} fullWidth size="small" />,
                                                }}
                                            />
                                        )}
                                    />
                                    <br />
                                    <br />
                                    <div className="form">
                                        <div className="form-left">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                Start Time
                                            </Typography>
                                            <TextField type="time" size="small" fullWidth {...form.register(`items.${index}.start_time`)} />
                                        </div>
                                        <div className="form-right">
                                            <Typography variant="inputLabel" color="text.secondary">
                                                End Time
                                            </Typography>
                                            <TextField
                                                type="time"
                                                size="small"
                                                fullWidth
                                                {...form.register(`items.${index}.end_time`)}
                                                error={Boolean(form.formState.errors.items?.[index]?.end_time)}
                                                helperText={form.formState.errors.items?.[index]?.end_time?.message}
                                            />
                                        </div>
                                    </div>
                                    <br />
                                </div>
                            </div>
                        ),
                    )}
                    <br />
                    {mobile ? (
                        <>
                            <br />
                            <br />
                            <br />
                            <br />
                        </>
                    ) : (
                        <div className="actions">
                            <Button variant="outlined" fullWidth onClick={() => setOpen(false)}>
                                Cancel
                            </Button>
                            <Button variant="contained" fullWidth onClick={form.handleSubmit(submit)} disabled={loading}>
                                Confirm Clock-Out {items?.length > 1 ? `(${items?.length})` : ''}
                            </Button>
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    )
}
